import { Link, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { Icon } from '@/lib/icons';
import { LanguageSelector } from '@/components/ui/language-selector';
import { YodiMark } from '@/components/brand/YodiMark';

const copy = {
  fr: {
    title: 'Des données vocales et textuelles pour que les langues ouest-africaines aient leur place dans l’IA.',
    description:
      'Yodi Data Hub est la plateforme de collecte d’Umbaji : enregistrement, transcription, traduction et validation, par et pour les communautés qui parlent ces langues.',
    platform: 'Plateforme',
    resources: 'Ressources',
    organization: 'Organisation',
    home: 'Accueil',
    leaderboard: 'Classement',
    createAccount: 'Créer un compte',
    signIn: 'Connexion',
    complaints: 'Réclamations',
    organizationAccess: 'Demande d’accès organisation',
    apiDocs: 'Documentation API',
    rights: 'Tous droits réservés.',
    bottom: 'Mission, produits et partenaires : retrouvez Umbaji sur umbaji.org.',
    themeLabel: 'Apparence',
    lightMode: 'Mode clair',
    darkMode: 'Mode sombre',
  },
  en: {
    title: 'Voice and text data so that West African languages get their place in AI.',
    description:
      'Yodi Data Hub is Umbaji’s collection platform: recording, transcription, translation and validation, by and for the communities who speak these languages.',
    platform: 'Platform',
    resources: 'Resources',
    organization: 'Organisation',
    home: 'Home',
    leaderboard: 'Leaderboard',
    createAccount: 'Create an account',
    signIn: 'Sign in',
    complaints: 'Complaints',
    organizationAccess: 'Organisation access request',
    apiDocs: 'API documentation',
    rights: 'All rights reserved.',
    bottom: 'Mission, products and partners: find Umbaji on umbaji.org.',
    themeLabel: 'Appearance',
    lightMode: 'Light mode',
    darkMode: 'Dark mode',
  },
} as const;

const linkClass = 'block w-fit text-muted-foreground transition-colors hover:text-foreground focus-ring';
const headingClass = 'mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-foreground/90';

export function Footer() {
  const { t, language, theme, toggleTheme } = useApp();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'fr';
  const isDark = theme === 'dark';
  const c = copy[language === 'en' ? 'en' : 'fr'];
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border/60 bg-background text-foreground">
      <div className="container mx-auto px-4 pb-20 pt-12 sm:px-6 sm:py-16">
        <div className="grid gap-10 lg:grid-cols-[1.3fr_repeat(3,0.7fr)]">
          <div className="space-y-6">
            <div className="space-y-4">
              <Link to={`/${currentLang}`} className="inline-flex items-center gap-3 rounded-xl focus-ring">
                <YodiMark className="size-9" />
                <span className="text-base font-semibold tracking-tight">
                  Yodi <span className="text-muted-foreground">Data Hub</span>
                </span>
              </Link>
              <h3 className="max-w-md text-xl font-semibold leading-snug text-foreground">{c.title}</h3>
              <p className="max-w-xl text-sm leading-7 text-muted-foreground">{c.description}</p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <LanguageSelector variant="footer" />

              <button
                type="button"
                onClick={toggleTheme}
                aria-label={isDark ? c.lightMode : c.darkMode}
                aria-pressed={!isDark}
                className="inline-flex h-11 w-fit items-center gap-3 rounded-full border border-border/60 px-2.5 pr-4 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-muted/60 focus-ring"
              >
                <span className="relative flex h-7 w-12 items-center rounded-full border border-border/60 bg-muted/40 p-0.5">
                  <span
                    className={`grid h-6 w-6 place-items-center rounded-full bg-background text-brand transition-transform duration-300 ease-premium ${
                      isDark ? 'translate-x-0' : 'translate-x-5'
                    }`}
                  >
                    <Icon name={isDark ? 'moon' : 'sun'} className="h-3.5 w-3.5" />
                  </span>
                </span>
                <span className="flex flex-col items-start leading-none">
                  <span className="text-[0.62rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{c.themeLabel}</span>
                  <span className="mt-1 text-xs font-semibold text-foreground">{isDark ? c.darkMode : c.lightMode}</span>
                </span>
              </button>
            </div>
          </div>

          <nav aria-label={c.platform}>
            <h4 className={headingClass}>{c.platform}</h4>
            <div className="space-y-3 text-sm">
              <Link to={`/${currentLang}`} className={linkClass}>{c.home}</Link>
              <Link to={`/${currentLang}/leaderboard`} className={linkClass}>{c.leaderboard}</Link>
              <Link to={`/${currentLang}/signup`} className={linkClass}>{c.createAccount}</Link>
              <Link to={`/${currentLang}/login`} className={linkClass}>{c.signIn}</Link>
            </div>
          </nav>

          <nav aria-label={c.resources}>
            <h4 className={headingClass}>{c.resources}</h4>
            <div className="space-y-3 text-sm">
              <Link to={`/${currentLang}/data-policy`} className={linkClass}>{t.dataPolicy}</Link>
              <Link to={`/${currentLang}/complaints`} className={linkClass}>{c.complaints}</Link>
              <Link to={`/${currentLang}/become-admin`} className={linkClass}>{c.organizationAccess}</Link>
            </div>
          </nav>

          <nav aria-label={c.organization}>
            <h4 className={headingClass}>{c.organization}</h4>
            <div className="space-y-3 text-sm">
              <a href="https://www.umbaji.org" target="_blank" rel="noopener noreferrer" className={linkClass}>
                umbaji.org
              </a>
              <a href="https://api.contributions.umbaji.xyz/docs" target="_blank" rel="noopener noreferrer" className={linkClass}>
                {c.apiDocs}
              </a>
              <a href="mailto:contact@umbaji.org" className={linkClass}>
                contact@umbaji.org
              </a>
            </div>
          </nav>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-border/60 pt-6 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {year} Umbaji · Yodi Data Hub. {c.rights}
          </p>
          <p className="max-w-xl sm:text-right">{c.bottom}</p>
        </div>
      </div>
    </footer>
  );
}
